import { currentMonth } from '../utils/contentStatus';

export const SNAPSHOT_KEY = 'content-studio-snapshot';
export const WORKING_KEY = 'content-working-client';

/**
 * Both keys are scoped to the signed-in user.
 *
 * They were global, and nothing cleared them on logout — so signing in as somebody
 * else on the same browser inherited THEIR working location, and the app then
 * loaded that client's data under the new user's session. The account store also
 * supports switching between saved accounts, which made a shared key actively
 * wrong rather than merely untidy.
 *
 * An empty userId falls back to the bare key only so a pre-auth read cannot throw;
 * nothing is expected to be found there.
 */
const scoped = (base, userId) => (userId ? base + ':' + userId : base);

export const snapshotKey = (userId) => scoped(SNAPSHOT_KEY, userId);
export const workingKey = (userId) => scoped(WORKING_KEY, userId);

/** Forget one user's studio state — call on logout. */
export function clearContentStudioState(userId) {
  try {
    localStorage.removeItem(snapshotKey(userId));
    localStorage.removeItem(workingKey(userId));
  } catch { /* ignore */ }
}

/**
 * Drop the pre-namespacing keys.
 *
 * Deliberately NOT migrated into the current user's namespace: the old value may
 * belong to whoever last used this browser, and silently adopting it is the exact
 * bug this change exists to stop. Removing them also clears the stale clientId
 * that outlived the scoping fix, so nobody has to clear it by hand.
 */
export function purgeLegacyContentStudioState() {
  try {
    localStorage.removeItem(SNAPSHOT_KEY);
    localStorage.removeItem(WORKING_KEY);
  } catch { /* ignore */ }
}

export function loadWorkingClient(userId) {
  try { return JSON.parse(localStorage.getItem(workingKey(userId)) || 'null'); } catch { return null; }
}

export function saveWorkingClient(client, userId) {
  try {
    if (client) localStorage.setItem(workingKey(userId), JSON.stringify(client));
    else localStorage.removeItem(workingKey(userId));
  } catch { /* ignore quota */ }
}

export const STEP_LABELS = {
  '/content/cockpit': 'Cockpit',
  '/content/ingestion': 'Data Ingestion',
  '/content/topics': 'Topics',
  '/content/pipeline': 'Articles',
  '/content/fanout': 'Distribute',
  '/content/handover': 'Deliver',
  '/content/history': 'History',
  '/content/team': 'Team',
  '/content/settings': 'Skills',
  '/content/catalog': 'Catalog',
};

export function stepLabel(path = '') {
  const base = String(path).split('?')[0];
  return STEP_LABELS[base] || 'Content Studio';
}

export function defaultSnapshot() {
  return {
    clientId: '',
    month: currentMonth(),
    pipeline: { topicId: '', articleId: '' },
    fanout: { articleId: '', topicId: '' },
    handover: { selected: '', filter: 'ready' },
    history: { ym: currentMonth(), selected: '' },
    registry: { search: '' },
    topics: { sourceType: 'meta', sourceName: '', newService: '' },
  };
}

function mergeSnapshot(prev, partial) {
  const next = { ...prev, ...partial };
  for (const key of ['pipeline', 'fanout', 'handover', 'history', 'registry', 'topics']) {
    if (partial[key]) next[key] = { ...prev[key], ...partial[key] };
  }
  return next;
}

export function loadSnapshot(userId) {
  try {
    const raw = JSON.parse(localStorage.getItem(snapshotKey(userId)) || '{}');
    return mergeSnapshot(defaultSnapshot(), raw);
  } catch {
    return defaultSnapshot();
  }
}

export function saveSnapshot(snapshot, userId) {
  try {
    localStorage.setItem(snapshotKey(userId), JSON.stringify(snapshot));
  } catch {
    /* ignore quota */
  }
}

export function patchSnapshot(partial, userId) {
  const next = mergeSnapshot(loadSnapshot(userId), partial);
  saveSnapshot(next, userId);
  return next;
}
